import { type TFunction } from 'i18next';

import { useTranslation } from '@cx-modules/i18n-provider';

import Arrow from '../../assets/Arrow';
import { useBookmakerLogo } from '../../hooks/useBookmakerLogo';
import { type OffersSlimVariant } from '../../utils/variants';

import { AutohideCard, CardContent, DefaultCard, Heading, LogoContainer } from './styles/Card';
import Cta from './styles/Cta';
import Text from './styles/Text';

const pickCardVariant = (variant?: OffersSlimVariant) => {
  switch (variant) {
    case 'autohide':
      return AutohideCard;

    case 'default':
    default:
      return DefaultCard;
  }
};

const parseLabel = (label?: string | string[]) => {
  if (Array.isArray(label)) {
    if (!label.length) {
      return null;
    }

    return label.join(' ');
  }

  return label ?? null;
};

export interface OfferCardProps {
  color: string;
  ctaText: string;
  disabled?: boolean;
  heading: string;
  id: string;
  label?: string;
  logoAlt: string;
  logoUrl: string;
  onClick: (...args: any) => void;
  text?: string;
  url?: string;
  variant?: OffersSlimVariant;
}

interface UseCtaLabel {
  fallback: string;
  key: string;
  translatorFunction: TFunction;
}

const useCtaLabel = ({ fallback, key, translatorFunction }: UseCtaLabel) => {
  const translation = translatorFunction(key);

  return translation === key ? fallback : translation;
};

export const OfferCard: React.FC<OfferCardProps> = ({
  color,
  ctaText,
  disabled,
  heading,
  id,
  label,
  logoAlt,
  logoUrl,
  onClick,
  text,
  variant,
}) => {
  const Card = pickCardVariant(variant);
  const offerLabelText = parseLabel(label);
  const transformedBookmakerLogoUrl = useBookmakerLogo(logoUrl);
  const { t } = useTranslation();
  const CTAlabel = useCtaLabel({ fallback: 'CLAIM', key: 'CX_CLAIM_OFFER_CTA', translatorFunction: t });

  return (
    <Card color={color} data-height={`${heading} !!`} data-testid="offer-card">
      <CardContent data-offer-id={id}>
        <LogoContainer>
          <img alt={logoAlt} src={transformedBookmakerLogoUrl} />
          {offerLabelText && <span>{offerLabelText}</span>}
        </LogoContainer>
        <Heading>
          {heading}
          <Arrow />
        </Heading>
        {text ? <Text dangerouslySetInnerHTML={{ __html: text }} /> : null}
        {!disabled ? (
          <Cta ctaLabel={CTAlabel} variant={variant}>
            <button
              onClick={passthrough => {
                passthrough.preventDefault();
                onClick(passthrough);
              }}
            >
              <span>{ctaText}</span>
            </button>
          </Cta>
        ) : null}
      </CardContent>
    </Card>
  );
};
